import { compareDesc, format, formatISO, isLeapYear, parseISO } from "date-fns";
import Decimal from "decimal.js";
import { DayCountEnum } from "gen/model/dayCountEnum";
import { isTruthy } from "shared/utils/utils";

export const DATETIME_FORMAT_DISPLAY = "M/d/yyyy h:mm a";
export const DATE_FORMAT_DISPLAY = "M/d/yyyy";
export const DATE_FORMAT_SUMMARY = "MMM dd, yyyy";

export const formatIsoDatetimeToDisplay = (
  isoDatetime: string | null | undefined,
) =>
  isoDatetime
    ? format(parseISO(isoDatetime), DATETIME_FORMAT_DISPLAY)
    : undefined;

/**
 * Format a date in ISO-8601 format to a display date format.
 *
 * @param isoDate Date in ISO-8601 format
 */
export const formatIsoDateToDisplay = (isoDate: string | null | undefined) =>
  isoDate ? format(parseISO(isoDate), DATE_FORMAT_DISPLAY) : undefined;

/**
 * Format a date in ISO-8601 format to a summary date format.
 *
 * @param isoDate Date in ISO-8601 format
 */
export const formatIsoDateToSummary = (isoDate: string) =>
  format(parseISO(isoDate), DATE_FORMAT_SUMMARY);

/**
 * Get the current date in ISO-8601 format.
 */
export const nowAsIsoDate = () =>
  formatISO(new Date(), { representation: "date" });

export const sortDateStringsDesc = (a: string, b: string) =>
  compareDesc(parseISO(a), parseISO(b));

export const formatDateRange = (
  startDate: string | undefined | null,
  endDate: string | undefined | null,
  fallback: string | null = "∞",
) =>
  [startDate, endDate]
    .map((d) => (d ? formatIsoDateToDisplay(d) : fallback))
    .filter(isTruthy)
    .join(" – ");

export const getDailyRateFromAnnualizedRate = (
  effectiveDate: Date,
  rate: Decimal,
  dayCount: DayCountEnum,
): Decimal | undefined => {
  if (dayCount === DayCountEnum.actual) {
    const numberOfDaysInYear = isLeapYear(effectiveDate) ? 366 : 365;
    return rate.div(numberOfDaysInYear);
  } else if (dayCount === DayCountEnum.actual_360) {
    return rate.div(360);
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  else if (dayCount === DayCountEnum.actual_365) {
    return rate.div(365);
  }
};

export const getAnnualizedRateFromDailyRate = (
  effectiveDate: Date,
  rate: Decimal,
  dayCount: DayCountEnum,
): Decimal | undefined => {
  if (dayCount === DayCountEnum.actual) {
    const numberOfDaysInYear = isLeapYear(effectiveDate) ? 366 : 365;
    return rate.times(numberOfDaysInYear);
  } else if (dayCount === DayCountEnum.actual_360) {
    return rate.times(360);
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  else if (dayCount === DayCountEnum.actual_365) {
    return rate.times(365);
  }
};

export const getQuarterFromDate = (date: string): string => {
  const parsedDate = parseISO(date);
  const month = parsedDate.getMonth() + 1;
  if (month <= 3) return "Q1";
  if (month <= 6) return "Q2";
  if (month <= 9) return "Q3";
  return "Q4";
};

export const getQuarterYearFromDate = (date: string): string =>
  `${getQuarterFromDate(date)} ${parseISO(date).getFullYear()}`;
